import Vue from 'vue';
import { VuexBuilder } from '@helpers/vuexBuilder';
import moment from 'moment';

const state = {
	certifications: [],
	certifications_loading: false,
	certifications_loaded: false,
	certifications_isSaving: false,
	operations: {
		get: false,
		list: false,
		uploading: false,
		downloading: false,
		reminding: false,
		deleting: false,
		updatingExpiry: false,
		uploadingFile: false,
		deletingFile: false
	}
};

const getters = {
	allCertifications: state => state.certifications,
	getCertifications: state => state.certifications.filter(x => !x.deleted),
	expiringSoon: (state, getters) => getters.getCertifications.filter(x => !!x.expiryDate && moment(x.expiryDate).isBefore(moment().add(14, 'days')) && moment(x.expiryDate).isAfter(moment())),
	expired: (state, getters) => getters.getCertifications.filter(x => !!x.expiryDate && moment(x.expiryDate).isBefore(moment())),
	deleted: state => state.certifications.filter(x => !!x.deleted),
	hasLoaded: state => state.certifications_loaded,
	isGetting: state => state.operations.get,
	isListing: state => state.operations.list,
	isUploading: state => state.operations.uploading,
	isDownloading: state => state.operations.downloading,
	isUploadingFile: state => state.operations.uploadingFile,
	isDeletingCertification: state => state.operations.deleting,
	isDeletingFile: state => state.operations.deletingFile,
	isReminding: state => state.operations.reminding,
	certificationsByEmployee: state => employeeId => {
		return state.certifications.filter(x => x.employeeId === employeeId);
	},
	isDeleted: (state, getters) => certificationId => {
		return getters.deleted.findIndex(x => x.id === certificationId) > -1;
	},
	isExpired: (state, getters) => certificationId => {
		return getters.expired.findIndex(x => x.id === certificationId) > -1;
	},
	isExpiringSoon: (state, getters) => certificationId => {
		return getters.expiringSoon.findIndex(x => x.id === certificationId) > -1;
	},
	getCertificationById: (state, getters) => certificationId => state.certifications.find(x => x.id === certificationId)
};

const actions = {
	async initialize({ commit, state, dispatch }) {
		if (!state.certifications_loaded && !state.certifications_loading) {
			commit('SET_LOADING', true);
			await dispatch('getActiveEmployee');
			commit('SET_LOADING', false);
		}
	},

	async getActiveEmployee({ commit, rootState }) {
		return await new VuexBuilder(commit)
			.operationKey('list')
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/certifications`))
			.commitData('GET_SUCCESS')
			.execute();
	},

	async uploadCertification({ commit, rootState }, uploadFormData) {
		return await new VuexBuilder(commit)
			.operationKey('uploading')
			.request(() =>
				Vue.axios.post(`/api/${rootState.tenantId}/certifications`, uploadFormData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				})
			)
			.successToast('Certification added successfully')
			.errorToast('Oops! We couldn’t upload the certification. Make sure the file is an accepted format and under the maximum size.')
			.commitData('UPDATE_SUCCESS')
			.execute();
	},
	async download({ commit, rootState }, { certificationId, fileId }) {
		await new VuexBuilder(commit)
			.operationKey('downloading')
			.request(() =>
				Vue.axios({
					url: `/api/${rootState.tenantId}/certifications/${certificationId}/files/${fileId}`,
					method: 'GET',
					responseType: 'blob'
				})
			)
			.errorToast('Failed to download file(s)')
			.download();
	},
	async sendReminder({ commit, rootState }, id) {
		return await new VuexBuilder(commit)
			.operationKey('reminding')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/certifications/${id}/sendReminder`))
			.successToast('Certification reminder sent successfully')
			.errorToast('Failed to send certification reminder')
			.commitData('REMINDER_SUCCESS', id)
			.execute();
	},
	async delete({ commit, rootState }, id) {
		return await new VuexBuilder(commit)
			.operationKey('deleting')
			.request(() => Vue.axios.delete(`/api/${rootState.tenantId}/certifications/${id}`))
			.successToast('Certification deleted successfully')
			.errorToast('Failed to delete certification')
			.commitData('DELETE_SUCCESS', id)
			.execute();
	},
	async deleteFile({ commit, rootState }, { certificationId, fileId }) {
		return await new VuexBuilder(commit)
			.operationKey('deletingFile')
			.request(() => Vue.axios.delete(`/api/${rootState.tenantId}/certifications/${certificationId}/files/${fileId}`))
			.successToast('File deleted successfully')
			.errorToast('Failed to delete file')
			.commitData('DELETE_FILE_SUCCESS', { certificationId, fileId })
			.execute();
	},
	async getAllVersionsByEmployee({ commit, rootState }, { employeeId, certificationTypeId }) {
		let url = `/api/${rootState.tenantId}/certifications/employee/${employeeId}`;
		if (certificationTypeId) {
			url += `?certificationType=${certificationTypeId}`;
		}

		return await new VuexBuilder(commit)
			.operationKey('list')
			.request(() => Vue.axios.get(url))
			.errorToast('Failed to get all certification history')
			.execute();
	},
	async updateExpiry({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey('updatingExpiry')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/certifications/${payload.id}/updateExpiry`, payload))
			.successToast('Certification expiry updated')
			.errorToast('Failed to update expiry')
			.commitData('UPDATE_EXPIRY_SUCCESS')
			.execute();
	},
	async uploadFiles({ commit, rootState }, uploadDto) {
		return await new VuexBuilder(commit)
			.operationKey('uploadingFile')
			.request(() =>
				Vue.axios.post(`/api/${rootState.tenantId}/certifications/${uploadDto.certificationId}/files`, uploadDto.document, {
					headers: { 'Content-Type': 'multipart/form-data' }
				})
			)
			.successToast('Certification file uploaded successfully')
			.errorToast('Oops! We couldn’t upload the certification. Make sure the file is an accepted format and under the maximum size.')
			.transformData(data =>
				data.map(x => {
					return { ...x, certificationId: uploadDto.certificationId };
				})
			)
			.commitData('UPLOAD_FILE_SUCCESS')
			.execute();
	}
};

const mutations = {
	SET_LOADING(state, loading) {
		state.certifications_loading = loading;
	},
	START_OPERATION(state, operationKey) {
		state.operations[operationKey] = true;
	},
	FINISH_OPERATION(state, operationKey) {
		state.operations[operationKey] = false;
	},
	GET_SUCCESS(state, data) {
		state.certifications_loaded = true;
		state.certifications = data;
	},
	UPDATE_SUCCESS(state, updated) {
		const updates = Array.isArray(updated) ? updated : [updated];
		for (const update of updates) {
			const index = state.certifications.findIndex(x => x.employeeId === update.employeeId && x.certificationTypeId === update.certificationTypeId);
			if (index > -1) {
				state.certifications.splice(index, 1, update);
			} else {
				state.certifications.push(update);
			}
		}
	},
	DELETE_SUCCESS(state, id) {
		const index = state.certifications.findIndex(x => x.id === id);
		if (index > -1) {
			state.certifications.splice(index, 1);
		}
	},
	REMINDER_SUCCESS(state, id) {
		const cert = state.certifications.find(x => x.id === id);
		if (!cert) return;
		cert.lastReminderUtc = moment().toISOString();
	},
	UPDATE_EXPIRY_SUCCESS(state, updated) {
		const index = state.certifications.findIndex(x => x.employeeId === updated.employeeId && x.id == updated.id);
		if (index > -1) {
			state.certifications.splice(index, 1, updated);
		}
	},
	UPLOAD_FILE_SUCCESS(state, uploads) {
		for (const upload of uploads) {
			const index = state.certifications.findIndex(x => x.id === upload.certificationId);
			if (index === -1) continue;
			state.certifications[index].files.push(upload);
		}
	},
	DELETE_FILE_SUCCESS(state, { certificationId, fileId}) {
		const index = state.certifications.findIndex(x => x.id === certificationId);
		if (index > -1) {
			const fileIndex = state.certifications[index].files.findIndex(x => x.id === fileId);
			if (fileIndex > -1) {
				state.certifications[index].files.splice(fileIndex, 1);
			}
		}
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
